import { z } from 'zod';

import { reportErrorToBugsnag } from '@air/utils-error';

import { describeZodIssues } from '~/components/v2/components/workspace/workspace-agent-panel/utils/describeZodIssues';
import { readWorkspaceAgentToolCallDescription } from '~/components/v2/components/workspace/workspace-agent-panel/utils/readWorkspaceAgentToolCallDescription';

const RawToolCallSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  toolName: z.string().optional(),
  arguments: z.record(z.string(), z.unknown()).default({}),
});

/** The backend has used both `name` and `toolName` for this field, so accept either. */
export const ToolCallSchema = RawToolCallSchema.transform((raw, ctx) => {
  const name = raw.name ?? raw.toolName;

  if (!name) {
    ctx.addIssue('toolCall requires name or toolName');

    return z.NEVER;
  }

  return {
    id: raw.id,
    name,
    description: readWorkspaceAgentToolCallDescription(raw.arguments),
    arguments: raw.arguments,
  };
});

export type ToolCall = z.infer<typeof ToolCallSchema>;

export const ToolCallMetadataSchema = z.object({
  toolCalls: z.array(z.unknown()),
});

export const parseToolCallMetadata = (metadata: unknown): ToolCall[] => {
  const parsed = ToolCallMetadataSchema.safeParse(metadata);

  if (!parsed.success) {
    // A `toolCall` message always carries this envelope, so failing here means every tool row
    // disappears from the transcript with nothing left to explain the gap. The metadata itself holds
    // the arguments the calls were made with, which are workspace content.
    reportErrorToBugsnag({
      error: new Error('Tool call metadata could not be parsed'),
      context: 'parseToolCallMetadata',
      metadata: { issues: describeZodIssues(parsed.error) },
    });

    return [];
  }

  // Per-item parse so one malformed tool call doesn't drop the rest of the list.
  return parsed.data.toolCalls
    .map((raw) => ToolCallSchema.safeParse(raw))
    .filter((result): result is { success: true; data: ToolCall } => result.success)
    .map((result) => result.data);
};
